import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, request, getStoredToken } from '@/lib/api';
import {
  ArrowLeft,
  Briefcase,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Printer,
  Shield,
  DollarSign,
  Scale,
  Users,
  FileText,
  Workflow,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import GrandmaLetter from '@/components/report/GrandmaLetter';
import ReportFooter from '@/components/report/ReportFooter';
import { getRawReportData } from '@/hooks/useReportData';
import { PageSpinner, PageEmptyState } from '@/components/ui/PageLayout';
import { ReportSubHeader } from '@/components/report/ReportSubHeader';
import { format } from 'date-fns';
import { transformRawApiData, type Finding } from '@/utils/scanToInsights';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// Local transformApiData removed — BusinessSummaryPage now uses the shared
// transformRawApiData from @/utils/scanToInsights so all sub-report pages
// produce consistent field values from the same underlying scan data.
// buildToolInfo remains page-specific: it derives the capability list / process
// flow that are only displayed in the Business Summary view.

interface ProcessFlowStep {
  step: number;
  title: string;
  description: string;
}

interface BusinessToolInfo {
  capabilities: string[];
  description: string;
  processFlow: ProcessFlowStep[] | null;
}

interface RawPermission {
  name?: string;
  permission?: string;
}

/** Shape of raw scan data fields accessed by buildToolInfo */
interface RawScanDataForToolInfo {
  toolInfo?: {
    capabilities?: string[];
    processFlow?: ProcessFlowStep[];
    [key: string]: unknown;
  };
  mcpTools?: { toolsList?: string[]; [key: string]: unknown };
  permissions?: (string | RawPermission)[];
  metadata?: {
    description?: string;
    capabilities?: string[];
    [key: string]: unknown;
  };
  findings?: Array<{ category?: string; [key: string]: unknown }>;
  plugin?: { description?: string; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * Build toolInfo with capabilities from available scan data.
 * Accepts loosely-typed raw API data and safely extracts tool information.
 */
function buildToolInfo(rawData: Record<string, unknown>): BusinessToolInfo {
  const data = rawData as RawScanDataForToolInfo;
  // If toolInfo already exists in raw data, use it
  if (data.toolInfo?.capabilities && data.toolInfo.capabilities.length > 0) {
    return {
      capabilities: data.toolInfo.capabilities,
      description: (data.toolInfo.description as string) || '',
      processFlow: data.toolInfo.processFlow || null,
    };
  }

  const capabilities: string[] = [];

  // Extract from mcpTools.toolsList
  const mcpToolsList: string[] = data.mcpTools?.toolsList || [];
  if (mcpToolsList.length > 0) {
    // Map tool names to human-readable capabilities
    const toolCapabilityMap: Record<string, string> = {
      Bash: 'Execute shell commands',
      bash: 'Execute shell commands',
      execute_command: 'Execute system commands',
      Read: 'Read files from filesystem',
      read_file: 'Read files from filesystem',
      Write: 'Write files to filesystem',
      write_file: 'Write files to filesystem',
      Edit: 'Edit existing files',
      WebFetch: 'Fetch data from web URLs',
      web_fetch: 'Fetch data from web URLs',
      WebSearch: 'Search the web',
      web_search: 'Search the web',
      Task: 'Spawn sub-agents for tasks',
      Glob: 'Search for files by pattern',
      Grep: 'Search file contents',
      LS: 'List directory contents',
      TodoRead: 'Read todo items',
      TodoWrite: 'Write todo items',
      NotebookRead: 'Read notebook files',
      NotebookEdit: 'Edit notebook files',
    };

    mcpToolsList.forEach((tool: string) => {
      if (toolCapabilityMap[tool]) {
        capabilities.push(toolCapabilityMap[tool]);
      } else {
        // Format unknown tools nicely
        const formatted = tool
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toLowerCase()
          .replace(/^\w/, (c: string) => c.toUpperCase());
        capabilities.push(formatted);
      }
    });
  }

  // Extract from permissions array
  const permissions: (string | RawPermission)[] = data.permissions || [];
  if (permissions.length > 0 && capabilities.length === 0) {
    permissions.forEach((perm) => {
      if (typeof perm === 'string') {
        capabilities.push(perm);
      } else if (perm.name || perm.permission) {
        capabilities.push(perm.name || perm.permission || '');
      }
    });
  }

  // Extract from metadata.capabilities
  const metaCapabilities: string[] = data.metadata?.capabilities || [];
  if (metaCapabilities.length > 0) {
    metaCapabilities.forEach((cap) => {
      if (!capabilities.includes(cap)) {
        capabilities.push(cap);
      }
    });
  }

  // Infer capabilities from findings if still empty
  const rawFindings = data.findings || [];
  if (capabilities.length === 0 && rawFindings.length > 0) {
    const findingCategories = new Set<string>();
    rawFindings.forEach((f) => {
      if (f.category) findingCategories.add(f.category);
    });

    // Map finding categories to implied capabilities
    if (findingCategories.has('injection') || findingCategories.has('INJECTION')) {
      capabilities.push('Process user input');
    }
    if (findingCategories.has('secrets') || findingCategories.has('SECRETS')) {
      capabilities.push('Handle credentials/secrets');
    }
    if (findingCategories.has('code') || findingCategories.has('CODE')) {
      capabilities.push('Execute code operations');
    }
    if (findingCategories.has('ai') || findingCategories.has('AI')) {
      capabilities.push('AI model interactions');
    }
    if (findingCategories.has('network') || findingCategories.has('NETWORK')) {
      capabilities.push('Network communications');
    }
  }

  // Remove duplicates
  const uniqueCapabilities = [...new Set(capabilities)];

  return {
    capabilities: uniqueCapabilities,
    description: data.metadata?.description || data.plugin?.description || '',
    processFlow: data.toolInfo?.processFlow ?? null,
  };
}

const BusinessSummaryPage = () => {
  useDocumentTitle('Business Summary');
  const reportRef = useRef<HTMLDivElement>(null);
  const [printTimestamp] = useState<Date>(new Date());
  const windowData = getRawReportData();

  // Load dynamic report data
  const { data: fetchedData, isLoading: loading } = useQuery({
    queryKey: ['business-summary-report'],
    queryFn: async () => {
      // Priority 1: Window data (injected)
      if (windowData) return windowData;

      const urlParams = new URLSearchParams(window.location.search);

      // Priority 2: Token-based fetch (report-sharing token from URL)
      const reportToken = urlParams.get('token');
      if (reportToken) {
        try {
          const response = await request<Record<string, unknown>>(
            'GET',
            `/v2/reports/view?token=${reportToken}`,
            { token: getStoredToken() }
          );
          const scanData = (response.scan || response.data || response) as Record<string, unknown>;
          if (scanData && scanData.scanId) return scanData;
        } catch (e) {
          console.warn('[BusinessSummary] Report token fetch error:', e instanceof Error ? e.message : 'Unknown error');
        }
      }

      // Priority 3: Base64-encoded data in URL
      const base64Data = urlParams.get('data');
      if (base64Data) {
        try {
          const urlData = JSON.parse(atob(base64Data)) as Record<string, unknown>;
          if (urlData && urlData.scanId) return urlData;
        } catch (e) {
          console.warn('[BusinessSummary] Failed to parse base64 data:', e instanceof Error ? e.message : 'Unknown error');
        }
      }

      // Priority 4: Fetch by scanId from API
      const scanId = urlParams.get('scan_id') || urlParams.get('scanId') || urlParams.get('id');
      if (scanId) {
        try {
          const result = await apiClient.getScanFull(scanId);
          const scanData = (result.scan || result.data || result) as Record<string, unknown>;
          if (scanData) return scanData;
        } catch (e) {
          console.warn('[BusinessSummary] API fetch error:', e instanceof Error ? e.message : 'Unknown error');
        }
      }

      // Priority 5: Fetch latest scan
      try {
        const result = await apiClient.getScanHistoryFull(1);
        const scans = (result.scans || result.data || []) as unknown[];
        if (scans.length > 0) return scans[0];
      } catch (e) {
        console.warn('[BusinessSummary] Latest scan fetch error:', e instanceof Error ? e.message : 'Unknown error');
      }

      return null;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const _rawData = (fetchedData || windowData) as Record<string, unknown> | null;
  const dynamicData = _rawData
    ? { ...transformRawApiData(_rawData), toolInfo: buildToolInfo(_rawData) }
    : null;

  // Extract toolInfo for process flow and description
  const toolInfo = dynamicData?.toolInfo || {};
  const toolDescription = toolInfo.description || dynamicData?.metadata?.description || '';

  // Show loading spinner
  if (loading) {
    return <PageSpinner label="Loading business summary…" />;
  }

  // Show no-data message if no real data available
  if (!dynamicData) {
    return (
      <PageEmptyState
        icon={Briefcase}
        title="No Report Data Available"
        description="Run a security scan to generate a business summary report."
        action={
          <a href="/report" className="inline-flex items-center text-primary hover:underline">
            Close
          </a>
        }
      />
    );
  }

  const reportData = dynamicData;
  const pluginName = dynamicData?.pluginName || dynamicData?.plugin?.name || reportData.pluginName;

  // MCP Tools data for process flow - MUST be defined before processFlow uses them
  const mcpTools = dynamicData?.mcpTools || {};
  const toolsList: string[] = mcpTools.toolsList || toolInfo.capabilities || [];
  const hasNetworkAccess = toolsList.some((t: string) =>
    ['WebFetch', 'WebSearch', 'web_fetch', 'web_search', 'fetch', 'http'].includes(t)
  );
  const hasFileAccess = toolsList.some((t: string) =>
    ['Read', 'Write', 'Edit', 'Glob', 'Grep', 'read_file', 'write_file', 'read', 'write'].includes(
      t
    )
  );
  const hasBashAccess = toolsList.some((t: string) =>
    ['Bash', 'bash', 'execute_command', 'shell', 'terminal', 'exec'].includes(t)
  );
  const hasAgentSpawning = toolsList.some((t: string) =>
    ['Task', 'task', 'spawn_agent', 'dispatch_agent', 'agent', 'orchestrate'].includes(t)
  );

  const handlePrint = () => {
    window.print();
  };

  // Translate technical risk to business terms
  const getBusinessRiskLevel = (level: string) => {
    switch (level) {
      case 'critical':
        return {
          label: 'Unacceptable Risk',
          color: 'text-[hsl(var(--risk-critical))]',
          bg: 'bg-[hsl(var(--risk-critical-bg))]',
          border: 'border-[hsl(var(--risk-critical)/0.3)]',
        };
      case 'high':
        return {
          label: 'Significant Risk',
          color: 'text-[hsl(var(--risk-high))]',
          bg: 'bg-[hsl(var(--risk-high-bg))]',
          border: 'border-[hsl(var(--risk-high)/0.3)]',
        };
      case 'medium':
        return {
          label: 'Moderate Risk',
          color: 'text-[hsl(var(--risk-medium))]',
          bg: 'bg-[hsl(var(--risk-medium-bg))]',
          border: 'border-[hsl(var(--risk-medium)/0.3)]',
        };
      case 'low':
        return {
          label: 'Acceptable Risk',
          color: 'text-[hsl(var(--risk-low))]',
          bg: 'bg-[hsl(var(--risk-low-bg))]',
          border: 'border-[hsl(var(--risk-low)/0.3)]',
        };
      default:
        return {
          label: 'Under Review',
          color: 'text-[hsl(var(--risk-info))]',
          bg: 'bg-[hsl(var(--risk-info-bg))]',
          border: 'border-[hsl(var(--risk-info)/0.3)]',
        };
    }
  };

  const hasBlockingIssues =
    reportData.vulnerabilities.critical > 0 || reportData.vulnerabilities.high > 0;
  const effectiveRiskLevel = hasBlockingIssues
    ? reportData.vulnerabilities.critical > 0
      ? 'critical'
      : 'high'
    : reportData.scores.aivss.level;
  const overallRisk = getBusinessRiskLevel(effectiveRiskLevel);

  // Business impact items - derive severity from vulnerability counts
  const hasCritical = reportData.vulnerabilities.critical > 0;
  const hasHigh = reportData.vulnerabilities.high > 0;

  // Generate scan-specific business impacts
  const findingCategories = [
    ...new Set(((dynamicData?.findings || []) as Finding[]).map((f) => f.category || 'general')),
  ];
  const hasSecrets = findingCategories.includes('secrets');
  const hasInjection = findingCategories.includes('injection');
  const hasAI = findingCategories.includes('ai');
  const hasCode = findingCategories.includes('code');

  const businessImpacts = [
    {
      icon: DollarSign,
      title: 'Financial Exposure',
      description: hasCritical
        ? `${reportData.vulnerabilities.critical} critical vulnerabilities could lead to data breaches, regulatory fines, or operational disruption.`
        : hasHigh
          ? `${reportData.vulnerabilities.high} high-severity issues may result in moderate financial risk if exploited.`
          : 'Financial risk is within acceptable limits based on current findings.',
      severity: hasCritical ? 'high' : hasHigh ? 'moderate' : 'low',
    },
    {
      icon: Scale,
      title: 'Regulatory Compliance',
      description: hasSecrets
        ? `Secrets/credential issues detected may violate data protection regulations (GDPR, SOC 2, HIPAA).`
        : hasInjection
          ? `Injection vulnerabilities could expose protected data, affecting compliance posture.`
          : hasCritical || hasHigh
            ? 'Security gaps may require disclosure to auditors or affect compliance certifications.'
            : 'No significant compliance concerns identified in this scan.',
      severity:
        hasSecrets || (hasCritical && hasInjection)
          ? 'high'
          : hasCritical || hasHigh
            ? 'moderate'
            : 'low',
    },
    {
      icon: Users,
      title: 'Customer Trust',
      description:
        hasCritical && hasSecrets
          ? `Critical credential exposure risks could lead to customer data breaches and reputation damage.`
          : hasCritical
            ? `${reportData.vulnerabilities.critical} critical issues could impact service reliability and customer confidence.`
            : hasHigh
              ? 'High-severity findings should be addressed to maintain customer trust.'
              : 'Customer-facing risk is minimal based on current findings.',
      severity: hasCritical && hasSecrets ? 'high' : hasCritical ? 'moderate' : 'low',
    },
    {
      icon: Briefcase,
      title: 'Operational Continuity',
      description: hasAI
        ? `AI safety issues detected may cause unpredictable behavior in production environments.`
        : hasCode
          ? `Code quality issues (${reportData.vulnerabilities.medium + reportData.vulnerabilities.low} medium/low) may affect long-term maintainability.`
          : hasCritical
            ? 'Critical vulnerabilities could cause service disruption if exploited.'
            : 'Operational risk is acceptable for controlled deployment.',
      severity: hasAI && hasCritical ? 'high' : hasCritical ? 'moderate' : 'low',
    },
  ];

  // Plain language key findings
  // Generate findings from actual scan data
  const actualFindings: Finding[] = (dynamicData?.findings || []) as Finding[];
  const criticalFindings = actualFindings.filter((f) => f.severity === 'critical');
  const highFindings = actualFindings.filter((f) => f.severity === 'high');
  const secretFindings = actualFindings.filter((f) => f.category === 'secrets');
  const injectionFindings = actualFindings.filter((f) => f.category === 'injection');

  const executiveFindings = [
    {
      finding:
        criticalFindings.length > 0
          ? `${criticalFindings.length} Critical Vulnerabilities`
          : 'Security Assessment',
      status:
        criticalFindings.length > 0
          ? 'concern'
          : highFindings.length > 5
            ? 'concern'
            : 'acceptable',
      explanation:
        criticalFindings.length > 0
          ? `Critical issues found: ${criticalFindings
              .slice(0, 3)
              .map((f) => f.title)
              .join(
                ', '
              )}${criticalFindings.length > 3 ? ` and ${criticalFindings.length - 3} more` : ''}.`
          : highFindings.length > 0
            ? `${highFindings.length} high-severity issues found, no critical vulnerabilities.`
            : 'No critical or high-severity vulnerabilities detected in scan.',
    },
    {
      finding: 'VendorGuard (Creator Verification)',
      status: reportData.creator.verified ? 'acceptable' : 'concern',
      explanation: reportData.creator.verified
        ? `Creator "${reportData.creator.name}" verified with ${reportData.creator.trustScore}/100 trust score.`
        : `Creator "${reportData.creator.name || 'unknown'}" not fully verified - increases third-party risk.`,
    },
    {
      finding:
        secretFindings.length > 0
          ? `${secretFindings.length} Credential Issues`
          : injectionFindings.length > 0
            ? `${injectionFindings.length} Injection Vulnerabilities`
            : 'Data Security',
      status:
        secretFindings.length > 0
          ? 'concern'
          : injectionFindings.length > 0
            ? 'concern'
            : 'acceptable',
      explanation:
        secretFindings.length > 0
          ? `Potential credential/secret exposures: ${secretFindings
              .slice(0, 2)
              .map((f) => f.title)
              .join(', ')}.`
          : injectionFindings.length > 0
            ? `Injection risks found: ${injectionFindings
                .slice(0, 2)
                .map((f) => f.title)
                .join(', ')}.`
            : 'No credential exposures or injection vulnerabilities detected.',
    },
    {
      finding: 'Code Analysis',
      status:
        reportData.vulnerabilities.critical === 0 && reportData.vulnerabilities.high <= 3
          ? 'acceptable'
          : 'concern',
      explanation: `Scanned ${dynamicData?.metadata?.filesScanned || 'multiple'} files (${(dynamicData?.metadata?.linesScanned || 0).toLocaleString()} lines). Total issues: ${reportData.vulnerabilities.critical} critical, ${reportData.vulnerabilities.high} high, ${reportData.vulnerabilities.medium} medium.`,
    },
  ];

  // Process flow - use toolInfo from scan if available
  const processFlow =
    toolInfo.processFlow && toolInfo.processFlow.length > 0
      ? toolInfo.processFlow.map((step: ProcessFlowStep, idx: number) => ({
          step: step.step || idx + 1,
          title: step.title || 'Step ' + (idx + 1),
          description: step.description || '',
          example: toolInfo.capabilities
            ? 'Capabilities: ' + toolInfo.capabilities.slice(0, 3).join(', ')
            : '',
        }))
      : [
          {
            step: 1,
            title: 'User Request',
            description: 'An employee or automated system initiates a request to the AI agent',
            example: `Example: Request involving ${pluginName} capabilities`,
          },
          {
            step: 2,
            title: 'Agent Processing',
            description:
              'The AI agent interprets the request and determines which tools are needed',
            example: `The agent identifies that ${pluginName} provides ${toolsList.slice(0, 3).join(', ')} capabilities`,
          },
          {
            step: 3,
            title: 'Plugin Activation',
            description: `The ${pluginName} plugin is called to perform specific actions`,
            example: hasBashAccess
              ? 'Plugin may execute shell commands to accomplish tasks'
              : hasFileAccess
                ? 'Plugin accesses files and extracts content for analysis'
                : hasNetworkAccess
                  ? 'Plugin fetches external data or performs web searches'
                  : 'Plugin performs requested operations within its scope',
          },
          {
            step: 4,
            title: 'Data Processing',
            description:
              'The plugin processes data according to its configured permissions and capabilities',
            example: `${pluginName} has ${mcpTools.totalTools || toolsList.length} tools with ${mcpTools.highRiskTools || 0} high-risk capabilities`,
          },
          {
            step: 5,
            title: 'Result Delivery',
            description: hasAgentSpawning
              ? 'Results may be processed by sub-agents before final delivery'
              : 'Processed results are returned to the AI agent for final response generation',
            example: hasAgentSpawning
              ? 'Multi-agent orchestration may be involved in complex tasks'
              : 'The agent receives the data and generates the requested output',
          },
          {
            step: 6,
            title: 'User Output',
            description: 'The final response is delivered to the user or downstream system',
            example: 'User receives the requested information or action confirmation',
          },
        ];

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] text-zinc-100 print-container">
      <ReportSubHeader
        title="Business Summary Report"
        subtitle={reportData.pluginName || undefined}
        actions={
          <>
            <Button onClick={handlePrint} className="gap-2">
              <Printer className="w-4 h-4" />
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                window.close();
                setTimeout(() => {
                  window.location.href = '/reports';
                }, 300);
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Reports
            </Button>
          </>
        }
      />

      <div className="max-w-4xl mx-auto px-6 py-8 print-content" ref={reportRef}>
        {/* Cover Letter */}
        <div className="print-section mb-8 print:break-after-page">
          <GrandmaLetter
            pluginName={reportData.pluginName}
            toolDescription={toolDescription}
            vulnerabilities={reportData.vulnerabilities}
            recommendation={reportData.recommendation?.type || 'REVIEW_REQUIRED'}
            creatorVerified={reportData.creator?.verified || false}
            creatorName={reportData.creator?.name || 'Unknown'}
            hasSecrets={hasSecrets}
            hasInjection={hasInjection}
            reportDate={reportData.reportDate}
            scanType={dynamicData?.targetType || 'plugin'}
            targetUrl={dynamicData?.target || dynamicData?.metadata?.source || ''}
            isExternalTool={Boolean(
              dynamicData?.target?.includes('github.com') ||
              dynamicData?.target?.includes('npmjs.com') ||
              dynamicData?.target?.includes('pypi.org')
            )}
            metadata={{
              repoMetadata: dynamicData?.metadata?.repoMetadata || {},
              filesScanned: dynamicData?.metadata?.filesScanned || 0,
              linesScanned: dynamicData?.metadata?.linesScanned || 0,
            }}
            toolInfo={dynamicData?.toolInfo || {}}
          />
        </div>

        {/* Header */}
        <div className="print-section mb-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">Executive Business Summary</h1>
            <p className="text-lg text-muted-foreground">
              AI Tool Risk Assessment for {reportData.pluginName}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Report Date: {format(reportData.reportDate, 'MMMM d, yyyy')} | Report ID:{' '}
              {reportData.reportId}
            </p>
          </div>
        </div>

        {/* Overall Assessment Card */}
        <Card className={`mb-8 print-section ${overallRisk.border} border-2`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Overall Risk Assessment
                </p>
                <p className={`text-3xl font-bold ${overallRisk.color} mt-1`}>
                  {overallRisk.label}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${overallRisk.bg}`}>
                {hasCritical || hasHigh ? (
                  <AlertCircle className={`w-12 h-12 ${overallRisk.color}`} />
                ) : (
                  <Shield className={`w-12 h-12 ${overallRisk.color}`} />
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              {toolDescription && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">About This Tool:</p>
                  <p className="text-muted-foreground">{toolDescription}</p>
                </div>
              )}
              <p className="text-sm font-medium text-foreground mb-2">Executive Recommendation:</p>
              <p className="text-muted-foreground">{reportData.recommendation.summary}</p>
            </div>
          </CardContent>
        </Card>

        {/* What This Means For Your Business */}
        <section className="mb-8 print-section">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            What This Means For Your Business
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {businessImpacts.map((impact, index) => (
              <Card key={index} className="print-avoid-break">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        impact.severity === 'high'
                          ? 'bg-[hsl(var(--risk-critical-bg))]'
                          : impact.severity === 'moderate'
                            ? 'bg-[hsl(var(--risk-medium-bg))]'
                            : 'bg-[hsl(var(--risk-low-bg))]'
                      }`}
                    >
                      <impact.icon
                        className={`w-5 h-5 ${
                          impact.severity === 'high'
                            ? 'text-[hsl(var(--risk-critical))]'
                            : impact.severity === 'moderate'
                              ? 'text-[hsl(var(--risk-medium))]'
                              : 'text-[hsl(var(--risk-low))]'
                        }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{impact.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{impact.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Key Findings in Plain Language */}
        <section className="mb-8 print-section">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Key Findings
          </h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {executiveFindings.map((finding, index) => (
                  <div key={index} className="p-4 flex items-start gap-4 print-avoid-break">
                    <div
                      className={`p-2 rounded-full ${
                        finding.status === 'acceptable'
                          ? 'bg-[hsl(var(--risk-low-bg))]'
                          : 'bg-[hsl(var(--risk-medium-bg))]'
                      }`}
                    >
                      {finding.status === 'acceptable' ? (
                        <CheckCircle2 className="w-5 h-5 text-[hsl(var(--risk-low))]" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-[hsl(var(--risk-medium))]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground">{finding.finding}</h3>
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            finding.status === 'acceptable'
                              ? 'bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]'
                              : 'bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]'
                          }`}
                        >
                          {finding.status === 'acceptable' ? 'Acceptable' : 'Needs Attention'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{finding.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Quick Stats */}
        <section className="mb-8 print-section">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Risk Summary at a Glance
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center print-avoid-break">
              <CardContent className="p-4">
                <p className="text-3xl font-bold text-[hsl(var(--risk-critical))]">
                  {reportData.vulnerabilities.critical}
                </p>
                <p className="text-sm text-muted-foreground">Critical Issues</p>
              </CardContent>
            </Card>
            <Card className="text-center print-avoid-break">
              <CardContent className="p-4">
                <p className="text-3xl font-bold text-[hsl(var(--risk-high))]">
                  {reportData.vulnerabilities.high}
                </p>
                <p className="text-sm text-muted-foreground">High Priority</p>
              </CardContent>
            </Card>
            <Card className="text-center print-avoid-break">
              <CardContent className="p-4">
                <p className="text-3xl font-bold text-[hsl(var(--risk-medium))]">
                  {reportData.vulnerabilities.medium}
                </p>
                <p className="text-sm text-muted-foreground">Moderate Issues</p>
              </CardContent>
            </Card>
            <Card className="text-center print-avoid-break">
              <CardContent className="p-4">
                <p className="text-3xl font-bold text-[hsl(var(--risk-low))]">
                  {reportData.vulnerabilities.low}
                </p>
                <p className="text-sm text-muted-foreground">Low Priority</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Action Items */}
        <section className="mb-8 print-section">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            Recommended Actions
          </h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {reportData.recommendation.conditions.map((condition: string, index: number) => (
                  <div key={index} className="flex items-start gap-3 print-avoid-break">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">{index + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{condition}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Page Break before Appendix */}
        <div className="print-break-before"></div>

        {/* Appendix: Business Process Flow */}
        <section className="mb-8 print-section">
          <div className="border-t-4 border-primary pt-6 mb-6">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Appendix A: How This Tool Works
            </h2>
            <p className="text-muted-foreground">
              A simplified overview of the business process flow
            </p>
          </div>

          {/* Process Flow Diagram */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Workflow className="w-5 h-5 text-primary" />
                Business Process Flow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {processFlow.map(
                  (
                    step: {
                      step: number;
                      title: string;
                      description: string;
                      color: string;
                      example: string;
                    },
                    index: number
                  ) => (
                    <div key={step.step} className="print-avoid-break">
                      <div className="flex items-start gap-4">
                        {/* Step Number */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                            <span className="text-primary-foreground font-bold">{step.step}</span>
                          </div>
                          {index < processFlow.length - 1 && (
                            <div className="w-0.5 h-12 bg-primary/30 ml-5 my-1"></div>
                          )}
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 pb-4">
                          <h3 className="font-semibold text-foreground">{step.title}</h3>
                          <p className="text-muted-foreground mt-1">{step.description}</p>
                          <div className="mt-2 p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground italic">{step.example}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </CardContent>
          </Card>

          {/* Plugin Capabilities Summary */}
          <Card className="print-avoid-break">
            <CardHeader>
              <CardTitle className="text-lg">What {reportData.pluginName} Can Do</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[hsl(var(--risk-low))]" />
                    Detected Capabilities
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {(toolInfo.capabilities && toolInfo.capabilities.length > 0
                      ? toolInfo.capabilities.slice(0, 6)
                      : ['No capabilities detected from scan']
                    ).map((cap: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <ArrowRight className="w-3 h-3" />
                        {cap
                          .replace(/_/g, ' ')
                          .replace(/([A-Z])/g, ' $1')
                          .trim()}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-[hsl(var(--risk-critical))]" />
                    Security Concerns Found
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {(actualFindings.length > 0
                      ? actualFindings
                          .filter((f) => f.severity === 'critical' || f.severity === 'high')
                          .slice(0, 4)
                          .map((f) => f.title)
                      : ['No critical security concerns identified']
                    ).map((concern: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <ArrowRight className="w-3 h-3" />
                        {concern}
                      </li>
                    ))}
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3" />
                      Cannot modify system configurations
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Glossary */}
        <section className="mb-8 print-section">
          <Card className="print-avoid-break">
            <CardHeader>
              <CardTitle className="text-lg">Appendix B: Glossary of Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-foreground">AI Agent</dt>
                  <dd className="text-muted-foreground">
                    An AI system that can perform tasks autonomously by using various tools and
                    plugins.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">MCP Plugin</dt>
                  <dd className="text-muted-foreground">
                    Model Context Protocol plugin - a standardized way for AI agents to connect to
                    external tools and services.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Risk Score</dt>
                  <dd className="text-muted-foreground">
                    A numerical rating (0-10) indicating the potential security risk, where higher
                    scores indicate greater risk.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Vulnerability</dt>
                  <dd className="text-muted-foreground">
                    A weakness in the tool that could potentially be exploited to cause harm or
                    unauthorized access.
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </section>

        {/* Footer */}
        <ReportFooter generatedDate={printTimestamp} showPrintInfo={true} />
      </div>
    </div>
  );
};

export default BusinessSummaryPage;
